import { EventListeners, MinzeElement, Reactive } from 'minze'
import { Locale } from '../../modules/locale'
import MainModule from '../../modules/main'
import { Routing } from '../../modules/routing'

Locale.getInstance().registerKeySet('fr', 'lvcAccountHome', {
  title: 'Indiquez vos intérêts culturels pour personnaliser<br> votre expérience sur La Vitrine!',
  newAccount: {
    title: 'Votre compte a bien été créé.',
    subtitle: 'Et maintenant?',
    menuItems: {
      discoverEventsTitle: 'Découvrir des évènements',
      discoverEventsDescription: ' ',
      accountTitle: 'Aller à mon compte',
      accountDescription: ' ',
    },
  },
})
Locale.getInstance().registerKeySet('en', 'lvcAccountHome', {
  title: 'Share your cultural interests to personalize<br> your experience on La Vitrine!',
  newAccount: {
    title: 'Your account has been successfully created.',
    subtitle: 'And now?',
    menuItems: {
      discoverEventsTitle: 'Discover events',
      discoverEventsDescription: ' ',
      accountTitle: 'Go to my account',
      accountDescription: ' ',
    },
  },
})

interface MenuItem {
  title: string
  description: string
  url: string
  icon: string
  proFeature: boolean
  id?: string
}

export interface LvcAccountHome {
  menuItems: Array<MenuItem>
  isPartner: boolean
  showBecomePartnerButton: boolean
  isNewAccount: boolean
}

export class LvcAccountHome extends MinzeElement {
  _authHandler: any = null

  reactive: Reactive = [
    ['menuItems', []],
    ['isPartner', false],
    ['showBecomePartnerButton', true],
    ['isNewAccount', false],
  ]

  handleAuthChanged = async (data: any): Promise<void> => {
    const mainModule = MainModule.getInstance()
    const isPartner = await mainModule.getAuth().isPartner()
    const isPartnerInProcess = await mainModule.getAuth().isPartnerInProcess()
    this.isPartner = isPartner

    if (data?.isLoggedIn && (isPartner || isPartnerInProcess)) {
      this.showBecomePartnerButton = false
    }
  }

  trackProfileCultureClick = async () => {
    await MainModule.getInstance().analyticsTrack('cta_mon_profil', {
      emplacement: 'mon_compte',
    })
  }

  trackProfileFavoritesClick = async () => {
    await MainModule.getInstance().analyticsTrack('cta_mes_favoris', {
      emplacement: 'mon_compte',
    })
  }

  trackProfileNotificationsClick = async () => {
    await MainModule.getInstance().analyticsTrack('cta_notifications', {
      emplacement: 'mon_compte',
    })
  }

  async onReady(): Promise<void> {
    const mainModule = MainModule.getInstance()
    const lang = mainModule.getLang()

    // We get the query parameter to know if the user comes from the sign up process and if the profile is completed
    const urlParams = new URLSearchParams(window.location.search)
    const profileCompleted = urlParams.get('profile-completed')
    if (profileCompleted !== null) this.isNewAccount = true

    this.menuItems = this.isNewAccount
      ? [
          {
            title: Locale.getKey('lvcAccountHome.newAccount.menuItems.discoverEventsTitle'),
            description: Locale.getKey(
              'lvcAccountHome.newAccount.menuItems.discoverEventsDescription'
            ),
            url: Routing.ROUTES[lang].searchResults,
            icon: '<lvc-account-theater-icon width="40px" height="40px"></lvc-account-theater-icon>',
            proFeature: false,
          },
          {
            title: Locale.getKey('lvcAccountHome.newAccount.menuItems.accountTitle'),
            description: Locale.getKey('lvcAccountHome.newAccount.menuItems.accountDescription'),
            url: Routing.ROUTES[lang].profile,
            icon: '<lvc-home-icon data-width="40px" data-height="40px"></lvc-home-icon>',
            proFeature: false,
          },
        ]
      : [
          {
            id: 'profile-culture',
            title: Locale.getKey('profile.culture'),
            description: '',
            url: Routing.ROUTES[lang].profileCulture,
            icon: '<lvc-account-theater-icon width="40px" height="40px"></lvc-account-theater-icon>',
            proFeature: false,
          },
          {
            id: 'profile-favorites',
            title: Locale.getKey('profile.favorites'),
            description: '',
            url: Routing.ROUTES[lang].profileFavorites,
            icon: '<lvc-account-heart-icon width="40px" height="40px"></lvc-account-heart-icon>',
            proFeature: false,
          },
          {
            id: 'profile-notifications-account-home',
            title: Locale.getKey('profile.notification'),
            description: '',
            url: Routing.ROUTES[lang].profileNotification,
            icon: '<lvc-account-bell-icon width="40px" height="40px"></lvc-account-bell-icon>',
            proFeature: false,
          },
        ]

    this._authHandler = this.handleAuthChanged.bind(this)
    mainModule.getEE().on('lvc:user_state_changed', this._authHandler.bind(this))
    this.handleAuthChanged({
      isLoggedIn: await mainModule.getAuth().isLoggedIn(),
    })
  }

  html = () => `
    <div class="account-home">
      ${
        this.isNewAccount
          ? /* html*/ `
        <div class="account-home-header">
          <lvc-charlie-waving-icon width="100%" height="auto"></lvc-charlie-waving-icon>
          <h2 class="account-home-header-title">${Locale.getKey('lvcAccountHome.newAccount.title')}</h2>
          <h2 class="account-home-header-subtitle">${Locale.getKey('lvcAccountHome.newAccount.subtitle')}</h2>
        </div>
      `
          : /* html*/ `
        <h2 class="account-home-title">${Locale.getKey('lvcAccountHome.title')}</h2>
      `
      }
      <ul class="account-home-menu">
        ${this.menuItems
          .map(item => {
            if (!item.proFeature || this.isPartner) {
              return `
              <li>
                <lvc-hand-drawn-button
                id="${item.id || ''}" 
                label="${item.title}" 
                description="${item.description}" 
                url="${item.url}">
                <span slot="icon">${item.icon}</span>
                </lvc-hand-drawn-button>
              </li>
            `
            }
          })
          .join('')}
      </ul>
    </div>
  `
  eventListeners: EventListeners = [
    ['#profile-culture', 'click', this.trackProfileCultureClick.bind(this)],
    ['#profile-favorites', 'click', this.trackProfileFavoritesClick.bind(this)],
    ['#profile-notifications-account-home', 'click', this.trackProfileNotificationsClick],
  ]

  css = () => `
    :host {}

    .account-home-header {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 5px; 
      margin: 0 0 20px;
      text-align: center;
    }

    .account-home-header-title {
      margin: 0;
      color: #1E007D;
      font-size: 2.250em;
      font-weight: 800;
      line-height: 120%;
    }

    .account-home-header-subtitle {
      margin: 0;
      color: #1E007D;
      font-size: 0.875em;
      font-weight: 500;
      line-height: 120%;
    }

    lvc-charlie-waving-icon {
      width: 200px;
    }

    .account-home-title {
      margin: 0 0 20px;
      color: #1E007D;
      font-size: 1em;
      font-weight: 800;
      line-height: 120%;
      text-align: left;
    }

    .account-home-menu {
      display: flex;
      flex-direction: column;
      gap: 15px;
      margin: 0;
      padding: 0;
      list-style: none;
    }

    @media only screen and (max-width: 991px) {
      .account-home-title br {
        display: none;
      }
    }

    @media only screen and (min-width: 992px) {
      .account-home-header {
        gap: 10px; 
        margin: 0 0 40px;
      }

      lvc-charlie-waving-icon {
        width: 340px;
      }

      .account-home-title {
        margin: 0 0 40px;
        font-size: 1.500em;
        text-align: center;
      }
    }
  `
}
